/**
 * UiSystemIcons.ts
 * Handcrafted Smooth Stylized 2.5D Vector / Cel-Shaded SVG Iconography Suite
 * for TopHud, NavigationDock, Decor Floating Dock, Modals, Action Buttons, and Toasts.
 *
 * Art Direction (Good Pizza, Great Pizza / Cozy 2.5D Life-Sim):
 * - Crisp deep-ink cel-shaded outlines (#3B2314 / #2A170D) with round caps & joins (smooth at any zoom)
 * - Multi-stop linear & radial gradients with unique prefix IDs (sys_*)
 * - Chunky 2.5D extruded 3D bottom/side bevels and drop shadows
 * - Glossy white specular rim highlights
 */

export const UI_SYSTEM_SVG_ICONS = {
  /** 2.5D Isometric Pocket Star Diorama Brand Crest (Cel-Shaded Shield + Cozy House + 3D Golden Star) */
  brandCrest: `<svg class="ui-svg-icon ui-crest-svg" viewBox="0 0 32 32" width="22" height="22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="sys_crest_shield" x1="4" y1="2" x2="28" y2="30" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#312e81"/>
        <stop offset="55%" stop-color="#1e1b4b"/>
        <stop offset="100%" stop-color="#0f172a"/>
      </linearGradient>
      <linearGradient id="sys_crest_roof" x1="7" y1="11" x2="25" y2="21" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fde047"/>
        <stop offset="50%" stop-color="#f59e0b"/>
        <stop offset="100%" stop-color="#b45309"/>
      </linearGradient>
      <linearGradient id="sys_crest_star" x1="11" y1="2" x2="21" y2="13" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="60%" stop-color="#facc15"/>
        <stop offset="100%" stop-color="#ea580c"/>
      </linearGradient>
    </defs>
    <!-- 3D Extruded Shield Base Shadow -->
    <polygon points="16,3.8 28.5,10.2 28.5,24.2 16,31 3.5,24.2 3.5,10.2" fill="#2A170D"/>
    <!-- Hexagonal Diorama Shield Body -->
    <polygon points="16,2 28,8.8 28,22.5 16,29.2 4,22.5 4,8.8" fill="url(#sys_crest_shield)" stroke="#3B2314" stroke-width="1.8" stroke-linejoin="round"/>
    <!-- Inner Gold Bevel Rim -->
    <polygon points="16,3.8 26.2,9.8 26.2,21.5 16,27.4 5.8,21.5 5.8,9.8" fill="none" stroke="#fbbf24" stroke-width="1.1" stroke-opacity="0.75" stroke-linejoin="round"/>
    <!-- 2.5D Isometric House Left & Right Walls -->
    <polygon points="9,17.2 16,21 16,27 9,23.2" fill="#38bdf8" stroke="#3B2314" stroke-width="1.4" stroke-linejoin="round"/>
    <polygon points="23,17.2 16,21 16,27 23,23.2" fill="#0284c7" stroke="#3B2314" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- 2.5D Isometric Pitched Roof -->
    <polygon points="16,11.8 7.5,16.5 16,21.2 24.5,16.5" fill="url(#sys_crest_roof)" stroke="#3B2314" stroke-width="1.5" stroke-linejoin="round"/>
    <!-- Warm Glowing Window -->
    <polygon points="11.2,19.6 14.3,21.3 14.3,24.2 11.2,22.5" fill="#fef08a" stroke="#3B2314" stroke-width="1.1" stroke-linejoin="round"/>
    <!-- North Star Crown with Cel-Shaded Outline -->
    <path d="M16 2.5L17.4 6.4L21.3 7.7L17.4 9L16 12.9L14.6 9L10.7 7.7L14.6 6.4Z" fill="url(#sys_crest_star)" stroke="#3B2314" stroke-width="1.3" stroke-linejoin="round"/>
    <circle cx="16" cy="7.7" r="1.2" fill="#ffffff"/>
  </svg>`,

  /** Money / Cash (2.5D Cel-Shaded Emerald Banknote Stack & Gold Coin Emblem) */
  cash: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="sys_cash_bill" x1="3" y1="5" x2="25" y2="21" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#6ee7b7"/>
        <stop offset="45%" stop-color="#10b981"/>
        <stop offset="100%" stop-color="#047857"/>
      </linearGradient>
      <linearGradient id="sys_cash_band" x1="11" y1="6" x2="17" y2="22" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="55%" stop-color="#facc15"/>
        <stop offset="100%" stop-color="#d97706"/>
      </linearGradient>
    </defs>
    <!-- Chunky 3D Stack Bottom Extrusion -->
    <rect x="2.5" y="9" width="23" height="14.5" rx="4" fill="#064e3b" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <line x1="5" y1="21" x2="23" y2="21" stroke="#34d399" stroke-width="1" stroke-opacity="0.55"/>
    <!-- Top Crisp Banknote Face -->
    <rect x="2.5" y="5.5" width="23" height="14" rx="4" fill="url(#sys_cash_bill)" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <!-- Inner Decorative Border -->
    <rect x="4.8" y="7.6" width="18.4" height="9.8" rx="2.2" fill="none" stroke="#d1fae5" stroke-width="1" stroke-opacity="0.65"/>
    <!-- Golden Currency Strap Band -->
    <rect x="11.2" y="5.5" width="5.6" height="14" fill="url(#sys_cash_band)" stroke="#3B2314" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M11.2 19.5V23.5H16.8V19.5" fill="#b45309" stroke="#3B2314" stroke-width="1.4" stroke-linejoin="round"/>
    <!-- Central Gold Medallion Seal -->
    <circle cx="14" cy="12.5" r="3.6" fill="url(#sys_cash_band)" stroke="#3B2314" stroke-width="1.4"/>
    <circle cx="6.8" cy="12.5" r="1.2" fill="#d1fae5"/>
    <circle cx="21.2" cy="12.5" r="1.2" fill="#d1fae5"/>
    <!-- Top Specular Shine -->
    <path d="M5.5 7.2H10" stroke="#ffffff" stroke-width="1.3" stroke-linecap="round" opacity="0.85"/>
  </svg>`,

  /** Career / Work Briefcase (2.5D Cel-Shaded Portfolio Case with Brass Clasps) */
  briefcase: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="sys_brief_body" x1="3" y1="8" x2="25" y2="23" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#38bdf8"/>
        <stop offset="50%" stop-color="#0284c7"/>
        <stop offset="100%" stop-color="#0369a1"/>
      </linearGradient>
      <linearGradient id="sys_brief_flap" x1="3" y1="7" x2="25" y2="15" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#7dd3fc"/>
        <stop offset="100%" stop-color="#0ea5e9"/>
      </linearGradient>
    </defs>
    <!-- 2.5D Top Handle -->
    <path d="M9.5 8V5.4C9.5 4.1 10.6 3.2 11.9 3.2H16.1C17.4 3.2 18.5 4.1 18.5 5.4V8" stroke="#3B2314" stroke-width="3.2" stroke-linecap="round"/>
    <path d="M9.5 8V5.4C9.5 4.1 10.6 3.2 11.9 3.2H16.1C17.4 3.2 18.5 4.1 18.5 5.4V8" stroke="#fbbf24" stroke-width="1.6" stroke-linecap="round"/>
    <!-- Chunky 3D Bottom Block Shadow -->
    <rect x="3" y="10.5" width="22" height="14" rx="3.8" fill="#0c4a6e" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <!-- Main Case Body -->
    <rect x="3" y="7.8" width="22" height="13.8" rx="3.8" fill="url(#sys_brief_body)" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <!-- Upper Overlapping Cel-Shaded Flap -->
    <path d="M3 11.2C3 9.3 4.5 7.8 6.4 7.8H21.6C23.5 7.8 25 9.3 25 11.2V13.6C25 14.4 24.3 15 23.5 15H4.5C3.7 15 3 14.4 3 13.6V11.2Z" fill="url(#sys_brief_flap)" stroke="#3B2314" stroke-width="1.6" stroke-linejoin="round"/>
    <!-- Brass Locks -->
    <rect x="7.5" y="13.2" width="3.2" height="3.8" rx="1" fill="#facc15" stroke="#3B2314" stroke-width="1.3"/>
    <rect x="17.3" y="13.2" width="3.2" height="3.8" rx="1" fill="#facc15" stroke="#3B2314" stroke-width="1.3"/>
    <!-- Specular Highlight -->
    <path d="M6 9.6H14.5" stroke="#ffffff" stroke-width="1.3" stroke-linecap="round" opacity="0.85"/>
  </svg>`,

  /** Energy Bolt (Chunky 2.5D Cel-Shaded Thunderbolt with 3D Amber Bevel) */
  bolt: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="15" height="15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="sys_bolt_fill" x1="7" y1="2" x2="21" y2="25" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="48%" stop-color="#facc15"/>
        <stop offset="100%" stop-color="#f59e0b"/>
      </linearGradient>
    </defs>
    <!-- 3D Extruded Back/Bottom Offset -->
    <path d="M16.8 3.8L6.8 15.8H13.5L11.8 25.5L22.2 13.2H15.5L16.8 3.8Z" fill="#b45309" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <!-- Front Glossy Lightning Bolt -->
    <path d="M15.8 2.2L5.8 14.2H12.5L10.8 23.9L21.2 11.6H14.5L15.8 2.2Z" fill="url(#sys_bolt_fill)" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <!-- Crisp Specular Shine -->
    <path d="M14.2 5.2L8.4 12.4" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" opacity="0.9"/>
  </svg>`,

  /** Hunger Utensils / Gourmet Platter (2.5D Cel-Shaded Golden Cloche & Warm Platter) */
  utensils: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="15" height="15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="sys_uten_dome" x1="5" y1="7" x2="23" y2="19" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fde047"/>
        <stop offset="50%" stop-color="#f59e0b"/>
        <stop offset="100%" stop-color="#d97706"/>
      </linearGradient>
      <linearGradient id="sys_uten_plate" x1="3" y1="18" x2="25" y2="24" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#6ee7b7"/>
        <stop offset="100%" stop-color="#059669"/>
      </linearGradient>
    </defs>
    <!-- 3D Platter Base Shadow -->
    <path d="M3.5 19.5H24.5C24.5 22.2 21.8 24 18.5 24H9.5C6.2 24 3.5 22.2 3.5 19.5Z" fill="#065f46" stroke="#3B2314" stroke-width="1.6" stroke-linejoin="round"/>
    <!-- Cloche Top Knob -->
    <circle cx="14" cy="6.2" r="2.3" fill="#fef08a" stroke="#3B2314" stroke-width="1.5"/>
    <!-- Rounded 2.5D Gourmet Cloche Dome -->
    <path d="M5 17.5C5 11.8 9 7.8 14 7.8C19 7.8 23 11.8 23 17.5H5Z" fill="url(#sys_uten_dome)" stroke="#3B2314" stroke-width="1.6" stroke-linejoin="round"/>
    <!-- Emerald Platter Rim -->
    <rect x="2.5" y="17" width="23" height="3.4" rx="1.7" fill="url(#sys_uten_plate)" stroke="#3B2314" stroke-width="1.5"/>
    <!-- Glossy Dome Highlight -->
    <path d="M8.2 14.2C8.8 11.5 10.8 9.8 13.2 9.5" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" opacity="0.88"/>
  </svg>`,

  /** Happiness Smiles (2.5D Cel-Shaded Emote Medallions) */
  heartSmile: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="15" height="15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <radialGradient id="sys_mood_ecstatic" cx="12" cy="10" r="13" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#a5f3fc"/>
        <stop offset="55%" stop-color="#22d3ee"/>
        <stop offset="100%" stop-color="#0284c7"/>
      </radialGradient>
    </defs>
    <!-- 3D Extruded Bottom Rim -->
    <circle cx="14" cy="15.4" r="10.2" fill="#0369a1" stroke="#3B2314" stroke-width="1.7"/>
    <!-- Main Glossy Face Orb -->
    <circle cx="14" cy="13.5" r="10.2" fill="url(#sys_mood_ecstatic)" stroke="#3B2314" stroke-width="1.7"/>
    <!-- Rosy Blush Cheeks -->
    <ellipse cx="8.4" cy="15" rx="1.9" ry="1.1" fill="#fb7185" opacity="0.75"/>
    <ellipse cx="19.6" cy="15" rx="1.9" ry="1.1" fill="#fb7185" opacity="0.75"/>
    <!-- Happy Arc Eyes -->
    <path d="M8.8 11.8C9.4 10.4 10.8 10.4 11.4 11.8" stroke="#3B2314" stroke-width="1.8" stroke-linecap="round"/>
    <path d="M16.6 11.8C17.2 10.4 18.6 10.4 19.2 11.8" stroke="#3B2314" stroke-width="1.8" stroke-linecap="round"/>
    <!-- Big Joyful Smile -->
    <path d="M9.6 15.2C10.5 18.4 17.5 18.4 18.4 15.2Z" fill="#3B2314" stroke="#3B2314" stroke-width="1.4" stroke-linejoin="round"/>
    <path d="M11.5 16.8C12.5 17.6 15.5 17.6 16.5 16.8" stroke="#fb7185" stroke-width="1.5" stroke-linecap="round"/>
    <!-- Top Specular Highlight -->
    <path d="M8.5 7.2C10 5.8 12.2 5.3 14.2 5.5" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" opacity="0.85"/>
  </svg>`,

  moodContent: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="15" height="15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <radialGradient id="sys_mood_content" cx="12" cy="10" r="13" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#bae6fd"/>
        <stop offset="55%" stop-color="#38bdf8"/>
        <stop offset="100%" stop-color="#0284c7"/>
      </radialGradient>
    </defs>
    <circle cx="14" cy="15.4" r="10.2" fill="#075985" stroke="#3B2314" stroke-width="1.7"/>
    <circle cx="14" cy="13.5" r="10.2" fill="url(#sys_mood_content)" stroke="#3B2314" stroke-width="1.7"/>
    <circle cx="10.2" cy="11.8" r="1.4" fill="#3B2314"/>
    <circle cx="17.8" cy="11.8" r="1.4" fill="#3B2314"/>
    <path d="M10.2 15.8C11.4 17.4 16.6 17.4 17.8 15.8" stroke="#3B2314" stroke-width="1.9" stroke-linecap="round"/>
    <path d="M8.5 7.2C10 5.8 12.2 5.3 14.2 5.5" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" opacity="0.85"/>
  </svg>`,

  moodLow: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="15" height="15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <radialGradient id="sys_mood_low" cx="12" cy="10" r="13" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="55%" stop-color="#fbbf24"/>
        <stop offset="100%" stop-color="#d97706"/>
      </radialGradient>
    </defs>
    <circle cx="14" cy="15.4" r="10.2" fill="#92400e" stroke="#3B2314" stroke-width="1.7"/>
    <circle cx="14" cy="13.5" r="10.2" fill="url(#sys_mood_low)" stroke="#3B2314" stroke-width="1.7"/>
    <circle cx="10.2" cy="12" r="1.4" fill="#3B2314"/>
    <circle cx="17.8" cy="12" r="1.4" fill="#3B2314"/>
    <line x1="10.2" y1="16.8" x2="17.8" y2="16.8" stroke="#3B2314" stroke-width="1.9" stroke-linecap="round"/>
    <path d="M8.5 7.2C10 5.8 12.2 5.3 14.2 5.5" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" opacity="0.85"/>
  </svg>`,

  moodDepressed: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="15" height="15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <radialGradient id="sys_mood_dep" cx="12" cy="10" r="13" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fca5a5"/>
        <stop offset="55%" stop-color="#ef4444"/>
        <stop offset="100%" stop-color="#991b1b"/>
      </radialGradient>
    </defs>
    <circle cx="14" cy="15.4" r="10.2" fill="#7f1d1d" stroke="#3B2314" stroke-width="1.7"/>
    <circle cx="14" cy="13.5" r="10.2" fill="url(#sys_mood_dep)" stroke="#3B2314" stroke-width="1.7"/>
    <circle cx="10.2" cy="12" r="1.4" fill="#3B2314"/>
    <circle cx="17.8" cy="12" r="1.4" fill="#3B2314"/>
    <path d="M10.2 17.8C11.5 15.8 16.5 15.8 17.8 17.8" stroke="#3B2314" stroke-width="1.9" stroke-linecap="round"/>
    <!-- Cel-Shaded Tear Drop -->
    <path d="M19.8 13.8C19.8 13.8 21.6 16.2 20.5 17.4C19.6 18.2 18.2 17.4 18.8 15.8Z" fill="#38bdf8" stroke="#3B2314" stroke-width="1.1"/>
    <path d="M8.5 7.2C10 5.8 12.2 5.3 14.2 5.5" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" opacity="0.8"/>
  </svg>`,

  /** Day / Night Cycles (2.5D Cel-Shaded Weather Orbs) */
  sun: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="15" height="15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <radialGradient id="sys_tod_sun" cx="12" cy="11" r="8" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="60%" stop-color="#facc15"/>
        <stop offset="100%" stop-color="#ea580c"/>
      </radialGradient>
    </defs>
    <!-- Chunky Sun Rays -->
    <path d="M13 2.5V5M13 21V23.5M4.5 13H2M24 13H21.5M6.8 6.8L5 5M21 21L19.2 19.2M19.2 6.8L21 5M5 21L6.8 19.2" stroke="#fbbf24" stroke-width="2.2" stroke-linecap="round"/>
    <!-- 3D Sun Orb -->
    <circle cx="13" cy="13.8" r="6.4" fill="#b45309" stroke="#3B2314" stroke-width="1.6"/>
    <circle cx="13" cy="12.6" r="6.4" fill="url(#sys_tod_sun)" stroke="#3B2314" stroke-width="1.6"/>
    <!-- Puffy 2.5D Cel-Shaded Cloud in Foreground -->
    <path d="M13.5 21.8H22.2C24 21.8 25.2 20.5 25.2 19C25.2 17.6 24.1 16.5 22.7 16.4C22.2 14.4 20.4 13 18.2 13C15.8 13 13.9 14.8 13.7 17.1C12.4 17.3 11.5 18.3 11.5 19.6C11.5 20.9 12.4 21.8 13.5 21.8Z" fill="#ffffff" stroke="#3B2314" stroke-width="1.5" stroke-linejoin="round"/>
    <path d="M10.2 9.5C11 8.4 12.3 8 13.5 8.2" stroke="#ffffff" stroke-width="1.3" stroke-linecap="round" opacity="0.9"/>
  </svg>`,

  sunset: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="15" height="15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="sys_tod_sunset" x1="14" y1="6" x2="14" y2="20" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fde047"/>
        <stop offset="50%" stop-color="#f97316"/>
        <stop offset="100%" stop-color="#e11d48"/>
      </linearGradient>
    </defs>
    <!-- Warm Horizon Glow Rays -->
    <path d="M14 4V6.5M6.2 8.2L8 10M21.8 8.2L20 10" stroke="#fb7185" stroke-width="2" stroke-linecap="round"/>
    <!-- Setting Sun Half-Orb -->
    <path d="M6.2 18.5C6.2 14.2 9.7 10.7 14 10.7C18.3 10.7 21.8 14.2 21.8 18.5H6.2Z" fill="url(#sys_tod_sunset)" stroke="#3B2314" stroke-width="1.6" stroke-linejoin="round"/>
    <!-- Cel-Shaded Horizon Waves -->
    <path d="M2.5 18.5H25.5" stroke="#3B2314" stroke-width="2.2" stroke-linecap="round"/>
    <path d="M5 21.8H23" stroke="#fbbf24" stroke-width="2" stroke-linecap="round"/>
    <path d="M8.5 24.5H19.5" stroke="#f43f5e" stroke-width="1.8" stroke-linecap="round"/>
  </svg>`,

  moon: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="15" height="15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="sys_tod_moon" x1="6" y1="4" x2="22" y2="24" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="55%" stop-color="#38bdf8"/>
        <stop offset="100%" stop-color="#1d4ed8"/>
      </linearGradient>
    </defs>
    <!-- 3D Crescent Moon Extrusion -->
    <path d="M22.2 17.5C17.2 18.2 12.8 14.2 13.2 9C13.4 6.8 14.5 4.9 16.1 3.6C10.5 3.9 6 8.6 6 14.3C6 20.2 10.8 25 16.7 25C19.8 25 22.6 23.6 24.4 21.4C23.7 19.8 23 18.5 22.2 17.5Z" fill="#1e3a8a" stroke="#3B2314" stroke-width="1.6" stroke-linejoin="round"/>
    <!-- Front Glossy Crescent Moon -->
    <path d="M22.2 16C17.2 16.7 12.8 12.7 13.2 7.5C13.4 5.3 14.5 3.4 16.1 2.1C10.5 2.4 6 7.1 6 12.8C6 18.7 10.8 23.5 16.7 23.5C19.8 23.5 22.6 22.1 24.4 19.9C23.7 18.3 23 17 22.2 16Z" fill="url(#sys_tod_moon)" stroke="#3B2314" stroke-width="1.6" stroke-linejoin="round"/>
    <!-- Twinkling Golden Star -->
    <path d="M21 5L21.8 7.2L24 8L21.8 8.8L21 11L20.2 8.8L18 8L20.2 7.2Z" fill="#fde047" stroke="#3B2314" stroke-width="1.1" stroke-linejoin="round"/>
  </svg>`,

  /** Bottom Navigation Dock — 2.5D Cel-Shaded Folded Atlas Map & 3D Pin */
  navMap: `<svg class="ui-svg-icon nav-svg" viewBox="0 0 32 32" width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="sys_nav_map_l" x1="3" y1="8" x2="11" y2="27" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fde68a"/>
        <stop offset="100%" stop-color="#f59e0b"/>
      </linearGradient>
      <linearGradient id="sys_nav_map_m" x1="11" y1="6" x2="21" y2="27" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#6ee7b7"/>
        <stop offset="100%" stop-color="#059669"/>
      </linearGradient>
      <linearGradient id="sys_nav_map_r" x1="21" y1="6" x2="29" y2="27" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#7dd3fc"/>
        <stop offset="100%" stop-color="#0284c7"/>
      </linearGradient>
      <linearGradient id="sys_nav_pin" x1="16" y1="2" x2="25" y2="16" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fda4af"/>
        <stop offset="45%" stop-color="#f43f5e"/>
        <stop offset="100%" stop-color="#9f1239"/>
      </linearGradient>
    </defs>
    <!-- Chunky 3D Folded Map Base Extrusion -->
    <polygon points="3,11.5 11,8 21,11.5 29,8 29,26.8 21,30.2 11,26.8 3,30.2" fill="#2A170D" stroke="#2A170D" stroke-width="1.6" stroke-linejoin="round"/>
    <!-- Left Map Panel (Golden Coast) -->
    <polygon points="3,9 11,5.5 11,24.2 3,27.8" fill="url(#sys_nav_map_l)" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <!-- Center Map Panel (Emerald Heartland) -->
    <polygon points="11,5.5 21,9 21,27.8 11,24.2" fill="url(#sys_nav_map_m)" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <!-- Right Map Panel (Atlantic Sky) -->
    <polygon points="21,9 29,5.5 29,24.2 21,27.8" fill="url(#sys_nav_map_r)" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <!-- Dotted Route Trail on Map -->
    <path d="M6.5 20.5Q13 15 19.5 19.5T26 15" stroke="#ffffff" stroke-width="1.6" stroke-dasharray="2.2 2.2" stroke-linecap="round"/>
    <!-- 3D Floating Ruby Location Pin -->
    <ellipse cx="20.5" cy="17.8" rx="3.2" ry="1.4" fill="rgba(19,16,29,0.45)"/>
    <path d="M20.5 17C20.5 17 26 11.6 26 7.8C26 4.7 23.5 2.2 20.5 2.2C17.5 2.2 15 4.7 15 7.8C15 11.6 20.5 17 20.5 17Z" fill="url(#sys_nav_pin)" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <circle cx="20.5" cy="7.8" r="2.2" fill="#fef08a" stroke="#3B2314" stroke-width="1.2"/>
  </svg>`,

  /** Bottom Navigation Dock — 2.5D Cel-Shaded Cozy Armchair & Decor Brush */
  navDecor: `<svg class="ui-svg-icon nav-svg" viewBox="0 0 32 32" width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="sys_nav_sofa_back" x1="6" y1="6" x2="26" y2="20" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#e879f9"/>
        <stop offset="55%" stop-color="#a855f7"/>
        <stop offset="100%" stop-color="#6b21a8"/>
      </linearGradient>
      <linearGradient id="sys_nav_sofa_seat" x1="4" y1="13" x2="28" y2="25" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#f472b6"/>
        <stop offset="55%" stop-color="#db2777"/>
        <stop offset="100%" stop-color="#9d174d"/>
      </linearGradient>
    </defs>
    <!-- 3D Wooden Legs -->
    <path d="M7.5 23.5L6.2 28.5M24.5 23.5L25.8 28.5" stroke="#3B2314" stroke-width="3.6" stroke-linecap="round"/>
    <path d="M7.5 23.5L6.2 28.5M24.5 23.5L25.8 28.5" stroke="#f59e0b" stroke-width="1.8" stroke-linecap="round"/>
    <!-- Chunky 3D Sofa Base Extrusion -->
    <rect x="4" y="16" width="24" height="9.5" rx="4.2" fill="#581c87" stroke="#3B2314" stroke-width="1.7"/>
    <!-- Plush Tufted Backrest -->
    <rect x="6.5" y="6.5" width="19" height="12" rx="4.5" fill="url(#sys_nav_sofa_back)" stroke="#3B2314" stroke-width="1.7"/>
    <!-- Rolled 2.5D Armrests & Seat Cushion -->
    <path d="M4 15.2C4 13.4 5.4 12 7.2 12H9.5V17.5H22.5V12H24.8C26.6 12 28 13.4 28 15.2V20.5C28 22.4 26.4 24 24.5 24H7.5C5.6 24 4 22.4 4 20.5V15.2Z" fill="url(#sys_nav_sofa_seat)" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <!-- Golden Throw Pillow & Sparkle -->
    <rect x="11.5" y="12.5" width="9" height="5.5" rx="2.2" fill="#fde047" stroke="#3B2314" stroke-width="1.4"/>
    <path d="M25 3L26 5.6L28.6 6.6L26 7.6L25 10.2L24 7.6L21.4 6.6L24 5.6Z" fill="#fef08a" stroke="#3B2314" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="M9.5 9H17" stroke="#ffffff" stroke-width="1.3" stroke-linecap="round" opacity="0.8"/>
  </svg>`,

  /** Bottom Navigation Dock — 2.5D Cel-Shaded Check Shield */
  navCheck: `<svg class="ui-svg-icon nav-svg" viewBox="0 0 32 32" width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="sys_nav_chk" x1="5" y1="4" x2="27" y2="26" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#6ee7b7"/>
        <stop offset="50%" stop-color="#10b981"/>
        <stop offset="100%" stop-color="#047857"/>
      </linearGradient>
    </defs>
    <circle cx="16" cy="17.8" r="12" fill="#064e3b" stroke="#3B2314" stroke-width="1.8"/>
    <circle cx="16" cy="15.2" r="12" fill="url(#sys_nav_chk)" stroke="#3B2314" stroke-width="1.8"/>
    <path d="M10.5 15.5L14.2 19.2L21.8 11.5" stroke="#3B2314" stroke-width="4.2" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M10.5 15.5L14.2 19.2L21.8 11.5" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M9.5 7.5C11.5 6 14 5.5 16.5 5.8" stroke="#ffffff" stroke-width="1.4" stroke-linecap="round" opacity="0.85"/>
  </svg>`,

  /** Bottom Navigation Dock — 2.5D Cel-Shaded GPS Radar Compass */
  navRadar: `<svg class="ui-svg-icon nav-svg" viewBox="0 0 32 32" width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <radialGradient id="sys_nav_rad" cx="16" cy="15" r="12" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#0284c7"/>
        <stop offset="70%" stop-color="#0f172a"/>
        <stop offset="100%" stop-color="#1e1b4b"/>
      </radialGradient>
    </defs>
    <!-- 3D Extruded Bezel -->
    <circle cx="16" cy="17.8" r="12" fill="#0c4a6e" stroke="#3B2314" stroke-width="1.8"/>
    <circle cx="16" cy="15.2" r="12" fill="url(#sys_nav_rad)" stroke="#3B2314" stroke-width="1.8"/>
    <!-- Radar Sweep Cone -->
    <path d="M16 15.2L24.2 7.5A11.5 11.5 0 0 1 27.2 15.2H16Z" fill="rgba(56, 189, 248, 0.42)"/>
    <!-- Concentric Range Rings -->
    <circle cx="16" cy="15.2" r="7.2" stroke="#38bdf8" stroke-width="1.3" stroke-dasharray="2.5 2.5"/>
    <line x1="16" y1="15.2" x2="23.8" y2="7.8" stroke="#7dd3fc" stroke-width="2" stroke-linecap="round"/>
    <!-- Center Ping Dot -->
    <circle cx="16" cy="15.2" r="2.6" fill="#fde047" stroke="#3B2314" stroke-width="1.3"/>
    <circle cx="21" cy="11.5" r="1.6" fill="#34d399"/>
  </svg>`,

  /** Bottom Navigation Dock — 2.5D Isometric Cozy Home Villa */
  navHome: `<svg class="ui-svg-icon nav-svg" viewBox="0 0 32 32" width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="sys_nav_home_roof" x1="4" y1="4" x2="28" y2="17" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fb7185"/>
        <stop offset="50%" stop-color="#f43f5e"/>
        <stop offset="100%" stop-color="#be123c"/>
      </linearGradient>
      <linearGradient id="sys_nav_home_wall" x1="7" y1="14" x2="25" y2="28" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef3c7"/>
        <stop offset="65%" stop-color="#fde68a"/>
        <stop offset="100%" stop-color="#f59e0b"/>
      </linearGradient>
    </defs>
    <!-- 3D Foundation Block -->
    <rect x="6" y="24.5" width="20" height="4.2" rx="2" fill="#78350f" stroke="#3B2314" stroke-width="1.6"/>
    <!-- Chimney -->
    <rect x="20.5" y="6" width="4.2" height="7.5" rx="1" fill="#b45309" stroke="#3B2314" stroke-width="1.5"/>
    <!-- House Warm Cream Walls -->
    <path d="M7.5 14.5H24.5V26H7.5V14.5Z" fill="url(#sys_nav_home_wall)" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <!-- Chunky 2.5D Shingled Roof -->
    <path d="M3.2 15.2L16 4.5L28.8 15.2C29.4 15.8 29 16.8 28 16.8H4C3 16.8 2.6 15.8 3.2 15.2Z" fill="url(#sys_nav_home_roof)" stroke="#3B2314" stroke-width="1.8" stroke-linejoin="round"/>
    <!-- Arched Wooden Door & Glowing Window -->
    <path d="M11 26V19.8C11 18.5 12 17.5 13.3 17.5C14.6 17.5 15.6 18.5 15.6 19.8V26H11Z" fill="#b45309" stroke="#3B2314" stroke-width="1.5"/>
    <rect x="18" y="18" width="4.2" height="4.2" rx="1" fill="#38bdf8" stroke="#3B2314" stroke-width="1.4"/>
    <path d="M8.5 13.2L16 7" stroke="#ffffff" stroke-width="1.4" stroke-linecap="round" opacity="0.8"/>
  </svg>`,

  /** Trophy / Souvenir / Mastery (2.5D Cel-Shaded Golden Champion Cup) */
  trophy: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="sys_trophy_gold" x1="6" y1="3" x2="22" y2="20" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="50%" stop-color="#facc15"/>
        <stop offset="100%" stop-color="#d97706"/>
      </linearGradient>
    </defs>
    <!-- Handles -->
    <path d="M7.5 6.5H4.8C3.5 6.5 2.8 7.6 3.2 9.2C3.8 11.4 5.5 12.6 7.8 12.6" stroke="#3B2314" stroke-width="3.2" stroke-linecap="round"/>
    <path d="M7.5 6.5H4.8C3.5 6.5 2.8 7.6 3.2 9.2C3.8 11.4 5.5 12.6 7.8 12.6" stroke="#fbbf24" stroke-width="1.6" stroke-linecap="round"/>
    <path d="M20.5 6.5H23.2C24.5 6.5 25.2 7.6 24.8 9.2C24.2 11.4 22.5 12.6 20.2 12.6" stroke="#3B2314" stroke-width="3.2" stroke-linecap="round"/>
    <path d="M20.5 6.5H23.2C24.5 6.5 25.2 7.6 24.8 9.2C24.2 11.4 22.5 12.6 20.2 12.6" stroke="#fbbf24" stroke-width="1.6" stroke-linecap="round"/>
    <!-- Cup Stem & Pedestal -->
    <rect x="11.8" y="16.5" width="4.4" height="5" fill="#d97706" stroke="#3B2314" stroke-width="1.5"/>
    <rect x="7.5" y="21" width="13" height="4" rx="1.6" fill="#78350f" stroke="#3B2314" stroke-width="1.6"/>
    <!-- Main Cup Bowl -->
    <path d="M7 4H21V10.5C21 14.4 17.9 17.5 14 17.5C10.1 17.5 7 14.4 7 10.5V4Z" fill="url(#sys_trophy_gold)" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <path d="M9.5 6.2V10.5" stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" opacity="0.85"/>
  </svg>`,

  /** Refrigerator (2.5D Cel-Shaded Retro Cyan Fridge) */
  fridge: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="sys_fridge_body" x1="6" y1="3" x2="22" y2="25" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#a5f3fc"/>
        <stop offset="50%" stop-color="#38bdf8"/>
        <stop offset="100%" stop-color="#0284c7"/>
      </linearGradient>
    </defs>
    <rect x="6" y="4.5" width="16" height="21" rx="3.2" fill="#0369a1" stroke="#3B2314" stroke-width="1.7"/>
    <rect x="6" y="3" width="16" height="20.5" rx="3.2" fill="url(#sys_fridge_body)" stroke="#3B2314" stroke-width="1.7"/>
    <line x1="6" y1="11.2" x2="22" y2="11.2" stroke="#3B2314" stroke-width="1.6"/>
    <line x1="9.2" y1="6.2" x2="9.2" y2="9" stroke="#ffffff" stroke-width="2" stroke-linecap="round"/>
    <line x1="9.2" y1="13.8" x2="9.2" y2="18.2" stroke="#ffffff" stroke-width="2" stroke-linecap="round"/>
  </svg>`,

  /** PC Desk (2.5D Cel-Shaded Monitor & Desk) */
  desk: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="sys_desk_scr" x1="5" y1="4" x2="23" y2="17" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#7dd3fc"/>
        <stop offset="100%" stop-color="#0284c7"/>
      </linearGradient>
    </defs>
    <rect x="5" y="4" width="18" height="12.5" rx="2.5" fill="url(#sys_desk_scr)" stroke="#3B2314" stroke-width="1.7"/>
    <path d="M8.5 7.5L12 6.5M8.5 10.2L15 8.8" stroke="#ffffff" stroke-width="1.4" stroke-linecap="round" opacity="0.85"/>
    <rect x="11.8" y="16.5" width="4.4" height="4" fill="#64748b" stroke="#3B2314" stroke-width="1.5"/>
    <rect x="3" y="20.2" width="22" height="3.8" rx="1.6" fill="#d97706" stroke="#3B2314" stroke-width="1.6"/>
  </svg>`,

  /** Airplane / Flight Exploration (2.5D Cel-Shaded Jet) */
  plane: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="sys_plane_body" x1="4" y1="4" x2="24" y2="24" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#ffffff"/>
        <stop offset="55%" stop-color="#7dd3fc"/>
        <stop offset="100%" stop-color="#0284c7"/>
      </linearGradient>
    </defs>
    <path d="M21 22.5L18.8 13L23 8.8C24.8 7 25.2 4.8 23.6 3.2C22 1.6 19.8 2 18 3.8L13.8 8L4.2 5.8C3.2 5.6 2.2 6.5 2.8 7.5L8.2 14.8L4.2 18.8V21.8L8.8 20.6L12.8 24.6H15.8L17 20L24.2 25.2C25.2 25.8 26.2 24.8 25.8 23.8L21 22.5Z" fill="url(#sys_plane_body)" stroke="#3B2314" stroke-width="1.6" stroke-linejoin="round"/>
  </svg>`,

  /** Discount Ticket (2.5D Cel-Shaded Golden Voucher) */
  ticket: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="sys_ticket_fill" x1="3" y1="6" x2="25" y2="22" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="55%" stop-color="#fbbf24"/>
        <stop offset="100%" stop-color="#d97706"/>
      </linearGradient>
    </defs>
    <path d="M3 10.5A3.2 3.2 0 0 1 3 17.5V20.5A2.2 2.2 0 0 0 5.2 22.7H22.8A2.2 2.2 0 0 0 25 20.5V17.5A3.2 3.2 0 0 1 25 10.5V7.5A2.2 2.2 0 0 0 22.8 5.3H5.2A2.2 2.2 0 0 0 3 7.5V10.5Z" fill="url(#sys_ticket_fill)" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <line x1="10.5" y1="6.5" x2="10.5" y2="21.5" stroke="#3B2314" stroke-width="1.5" stroke-dasharray="2.2 2.2"/>
    <path d="M17.5 10.5L18.4 12.8L20.8 13.5L18.4 14.2L17.5 16.5L16.6 14.2L14.2 13.5L16.6 12.8Z" fill="#ffffff" stroke="#3B2314" stroke-width="1.1" stroke-linejoin="round"/>
  </svg>`,

  /** Radar / GPS Location Tracker */
  radar: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="14" cy="14" r="10.5" fill="#0f172a" stroke="#3B2314" stroke-width="1.7"/>
    <path d="M14 14L21.5 6.8A10.2 10.2 0 0 1 24.2 14H14Z" fill="rgba(56, 189, 248, 0.4)"/>
    <circle cx="14" cy="14" r="6" stroke="#38bdf8" stroke-width="1.3" stroke-dasharray="2.2 2.2"/>
    <circle cx="14" cy="14" r="2.2" fill="#fde047" stroke="#3B2314" stroke-width="1.2"/>
  </svg>`,

  /** Shield Protection (2.5D Cel-Shaded Emerald Aegis) */
  shield: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="sys_shield_fill" x1="5" y1="3" x2="23" y2="25" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#6ee7b7"/>
        <stop offset="55%" stop-color="#10b981"/>
        <stop offset="100%" stop-color="#047857"/>
      </linearGradient>
    </defs>
    <path d="M14 25C14 25 23 20.5 23 13.2V6.2L14 2.8L5 6.2V13.2C5 20.5 14 25 14 25Z" fill="url(#sys_shield_fill)" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <path d="M14 5.5V22.2" stroke="#ffffff" stroke-width="1.3" stroke-opacity="0.45"/>
  </svg>`,

  /** Sparkles / Buffs (2.5D Cel-Shaded Starburst) */
  sparkles: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="15" height="15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="sys_spark_fill" x1="4" y1="3" x2="24" y2="23" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="50%" stop-color="#f472b6"/>
        <stop offset="100%" stop-color="#db2777"/>
      </linearGradient>
    </defs>
    <path d="M13 3L15.4 10.2L22.5 12.6L15.4 15L13 22.2L10.6 15L3.5 12.6L10.6 10.2Z" fill="url(#sys_spark_fill)" stroke="#3B2314" stroke-width="1.6" stroke-linejoin="round"/>
    <path d="M21.5 17.5L22.4 20.2L25 21.1L22.4 22L21.5 24.6L20.6 22L18 21.1L20.6 20.2Z" fill="#fde047" stroke="#3B2314" stroke-width="1.3" stroke-linejoin="round"/>
  </svg>`,

  /** Academic / Graduation Cert Cap (2.5D Cel-Shaded Mortarboard) */
  academic: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="sys_acad_cap" x1="3" y1="5" x2="25" y2="16" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#38bdf8"/>
        <stop offset="100%" stop-color="#1d4ed8"/>
      </linearGradient>
    </defs>
    <path d="M7 12.5V19.5C7 22.2 10.2 24.2 14 24.2C17.8 24.2 21 22.2 21 19.5V12.5" fill="#1e3a8a" stroke="#3B2314" stroke-width="1.6" stroke-linejoin="round"/>
    <polygon points="14,4 2.5,9.8 14,15.6 25.5,9.8" fill="url(#sys_acad_cap)" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <path d="M23.5 10.8V18.5" stroke="#facc15" stroke-width="2" stroke-linecap="round"/>
    <circle cx="23.5" cy="19" r="1.5" fill="#facc15" stroke="#3B2314" stroke-width="1.1"/>
  </svg>`,

  /** Event Badges */
  badgeObstacle: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="15" height="15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.2 4.2L2.6 20.8C1.8 22.2 2.8 24 4.4 24H23.6C25.2 24 26.2 22.2 25.4 20.8L15.8 4.2C15 2.8 13 2.8 12.2 4.2Z" fill="#ef4444" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <line x1="14" y1="10" x2="14" y2="15.5" stroke="#ffffff" stroke-width="2.2" stroke-linecap="round"/>
    <circle cx="14" cy="19.5" r="1.4" fill="#ffffff"/>
  </svg>`,

  badgeWindfall: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="15" height="15" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polygon points="14,2.5 17.4,9.5 25,10.6 19.5,16 20.8,23.6 14,20 7.2,23.6 8.5,16 3,10.6 10.6,9.5" fill="#facc15" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
  </svg>`,

  /** Toast Status SVGs (2.5D Cel-Shaded Medallions) */
  toastInfo: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="14" cy="15.2" r="10.2" fill="#0369a1" stroke="#3B2314" stroke-width="1.6"/>
    <circle cx="14" cy="13.5" r="10.2" fill="#38bdf8" stroke="#3B2314" stroke-width="1.6"/>
    <circle cx="14" cy="9.2" r="1.4" fill="#ffffff"/>
    <line x1="14" y1="12.8" x2="14" y2="18.2" stroke="#ffffff" stroke-width="2.3" stroke-linecap="round"/>
  </svg>`,

  toastSuccess: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="14" cy="15.2" r="10.2" fill="#065f46" stroke="#3B2314" stroke-width="1.6"/>
    <circle cx="14" cy="13.5" r="10.2" fill="#10b981" stroke="#3B2314" stroke-width="1.6"/>
    <path d="M9.5 13.6L12.6 16.8L18.8 10.5" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`,

  toastWarning: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.2 4.5L2.6 21C1.8 22.4 2.8 24.2 4.4 24.2H23.6C25.2 24.2 26.2 22.4 25.4 21L15.8 4.5C15 3.1 13 3.1 12.2 4.5Z" fill="#f59e0b" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <line x1="14" y1="10.2" x2="14" y2="15.5" stroke="#3B2314" stroke-width="2.3" stroke-linecap="round"/>
    <circle cx="14" cy="19.5" r="1.5" fill="#3B2314"/>
  </svg>`,

  toastError: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="14" cy="15.2" r="10.2" fill="#7f1d1d" stroke="#3B2314" stroke-width="1.6"/>
    <circle cx="14" cy="13.5" r="10.2" fill="#ef4444" stroke="#3B2314" stroke-width="1.6"/>
    <path d="M17.2 10.3L10.8 16.7M10.8 10.3L17.2 16.7" stroke="#ffffff" stroke-width="2.3" stroke-linecap="round"/>
  </svg>`,

  /** Decor Floating Dock & Quick Action Cel-Shaded 2.5D Icons */
  decorShopBag: `<svg class="ui-svg-icon" viewBox="0 0 28 28" width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <defs>
      <linearGradient id="sys_shop_bag" x1="5" y1="8" x2="23" y2="25" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stop-color="#fef08a"/>
        <stop offset="50%" stop-color="#f59e0b"/>
        <stop offset="100%" stop-color="#b45309"/>
      </linearGradient>
    </defs>
    <path d="M9.8 9V6.5C9.8 4.3 11.7 2.6 14 2.6C16.3 2.6 18.2 4.3 18.2 6.5V9" stroke="#3B2314" stroke-width="2.2" stroke-linecap="round"/>
    <path d="M6.2 9H21.8L23.5 23.2C23.7 24.6 22.6 25.8 21.2 25.8H6.8C5.4 25.8 4.3 24.6 4.5 23.2L6.2 9Z" fill="url(#sys_shop_bag)" stroke="#3B2314" stroke-width="1.7" stroke-linejoin="round"/>
    <circle cx="10.2" cy="12" r="1.3" fill="#3B2314"/>
    <circle cx="17.8" cy="12" r="1.3" fill="#3B2314"/>
    <path d="M10.8 15.2C11.8 17 16.2 17 17.2 15.2" stroke="#3B2314" stroke-width="1.6" stroke-linecap="round"/>
  </svg>`,

  decorRotate: `<svg class="ui-svg-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M20 11A8 8 0 1 0 17.6 16.8" stroke="#38bdf8" stroke-width="2.4" stroke-linecap="round"/>
    <polygon points="21,6 21,12.5 14.5,12.5" fill="#7dd3fc" stroke="#3B2314" stroke-width="1.4" stroke-linejoin="round"/>
  </svg>`,

  decorUpgrade: `<svg class="ui-svg-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <polygon points="12,2.5 20.5,11.5 15.5,11.5 15.5,21 8.5,21 8.5,11.5 3.5,11.5" fill="#facc15" stroke="#3B2314" stroke-width="1.6" stroke-linejoin="round"/>
  </svg>`,

  decorPackBox: `<svg class="ui-svg-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <polygon points="12,3 21,7.5 12,12 3,7.5" fill="#fbbf24" stroke="#3B2314" stroke-width="1.5" stroke-linejoin="round"/>
    <polygon points="3,7.5 12,12 12,21 3,16.5" fill="#d97706" stroke="#3B2314" stroke-width="1.5" stroke-linejoin="round"/>
    <polygon points="21,7.5 12,12 12,21 21,16.5" fill="#b45309" stroke="#3B2314" stroke-width="1.5" stroke-linejoin="round"/>
  </svg>`,

  decorUndo: `<svg class="ui-svg-icon" viewBox="0 0 24 24" width="15" height="15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M9 14L4 9L9 4" stroke="#fca5a5" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M4 9H14.5C17.8 9 20.5 11.7 20.5 15C20.5 18.3 17.8 21 14.5 21H10" stroke="#fca5a5" stroke-width="2.4" stroke-linecap="round"/>
  </svg>`,
} as const;
